/**
 * Organization member roster — Stytch-sourced membership decorated with local
 * organization_role_grants.
 *
 * Presentational panel. Props-driven so Storybook can render empty / single /
 * mixed / paginated shapes without live Stytch. Live Settings container:
 * OrganizationAdminSection.tsx.
 */

import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@localagent/ui/components/table';
import type { OrgMember } from '@localagent/sdk';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

export interface OrganizationRosterProps {
  members: OrgMember[];
  isLoading?: boolean;
  error?: string | null;
  /** Authenticated but not an organization owner (expected muted state). */
  notOwner?: boolean;
  nextCursor?: string | null;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

export function OrganizationRoster({
  members,
  isLoading = false,
  error = null,
  notOwner = false,
  nextCursor = null,
  onLoadMore,
  loadingMore = false,
}: OrganizationRosterProps) {
  if (notOwner) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="org-roster-not-owner"
      >
        Only organization owners can view the organization member roster.
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive" data-testid="org-roster-error">
        {error}
      </p>
    );
  }

  if (isLoading) {
    return (
      <LoadingState data-testid="org-roster-loading" label="Loading organization members…" />
    );
  }

  return (
    <div className="space-y-3" data-testid="org-roster-panel">
      <div className="rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Organization role</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-center text-sm text-muted-foreground"
                >
                  <span data-testid="org-roster-empty">No members in this organization.</span>
                </TableCell>
              </TableRow>
            ) : (
              members.map((m) => (
                <TableRow
                  key={m.member_id}
                  data-testid={`org-roster-row-${m.member_id}`}
                >
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {m.email || m.member_id}
                      </span>
                      {m.name ? (
                        <span className="text-xs text-muted-foreground">{m.name}</span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      data-testid={`org-roster-status-${m.member_id}`}
                    >
                      {m.status || 'unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span data-testid={`org-roster-role-${m.member_id}`}>
                      {m.organization_role ?? '—'}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {nextCursor && onLoadMore ? (
        <Button
          size="sm"
          variant="outline"
          data-testid="org-roster-load-more"
          disabled={loadingMore}
          onClick={() => onLoadMore()}
        >
          {loadingMore ? 'Loading…' : 'Load more members'}
        </Button>
      ) : null}
    </div>
  );
}
